import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Store, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Merchant {
  user_id: string;
  cashtag: string;
  display_name: string;
  store_name: string | null;
  is_merchant: boolean;
  created_at: string;
}

interface AppDoc { path: string; name: string; type: string; size: number }
interface Application {
  id: string;
  user_id: string;
  store_name: string;
  category: string;
  description: string | null;
  website: string | null;
  phone: string | null;
  country: string;
  status: string;
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  documents: AppDoc[] | null;
}

const AdminMerchants = () => {
  const [tab, setTab] = useState("pending");
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [apps, setApps] = useState<Application[]>([]);
  const [appProfiles, setAppProfiles] = useState<Record<string, { cashtag: string; display_name: string }>>({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState<Application | null>(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const loadMerchants = async () => {
    let query = supabase.from("profiles")
      .select("user_id,cashtag,display_name,store_name,is_merchant,created_at")
      .eq("is_merchant", true).order("created_at", { ascending: false }).limit(100);
    if (q) query = query.or(`cashtag.ilike.%${q}%,display_name.ilike.%${q}%,store_name.ilike.%${q}%`);
    const { data, error } = await query;
    if (error) toast.error(error.message);
    setMerchants((data ?? []) as Merchant[]);
  };

  const loadApps = async (status: string) => {
    const { data, error } = await supabase
      .from("merchant_applications" as any)
      .select("*")
      .eq("status", status)
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) { toast.error(error.message); return; }
    const list = (data ?? []) as unknown as Application[];
    setApps(list);
    const ids = Array.from(new Set(list.map((a) => a.user_id)));
    if (ids.length) {
      const { data: profs } = await supabase.from("profiles")
        .select("user_id,cashtag,display_name").in("user_id", ids);
      const map: Record<string, { cashtag: string; display_name: string }> = {};
      (profs ?? []).forEach((p: any) => { map[p.user_id] = { cashtag: p.cashtag, display_name: p.display_name }; });
      setAppProfiles(map);
    }
  };

  const load = async () => {
    setLoading(true);
    if (tab === "active") await loadMerchants();
    else await loadApps(tab);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const review = async (decision: "approved" | "rejected") => {
    if (!reviewing) return;
    if (decision === "rejected" && notes.trim().length < 3) {
      toast.error("Provide a rejection reason");
      return;
    }
    setBusy(true);
    const { error } = await supabase.rpc("admin_review_merchant_application" as any, {
      _application_id: reviewing.id,
      _decision: decision,
      _notes: notes || null,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success(`Application ${decision}`);
    setReviewing(null); setNotes("");
    load();
  };

  const suspend = async (m: Merchant) => {
    const { error } = await supabase.from("profiles")
      .update({ is_merchant: false }).eq("user_id", m.user_id);
    if (error) { toast.error(error.message); return; }
    await supabase.rpc("log_admin_action", {
      _action: "suspend_merchant", _target_type: "user",
      _target_id: m.user_id, _metadata: { cashtag: m.cashtag },
    });
    toast.success(`Suspended @${m.cashtag}`);
    loadMerchants();
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Store className="h-5 w-5" /> Merchants
        </h1>
        <p className="text-sm text-muted-foreground">Review applications, manage active merchants.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="active">Active merchants</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="space-y-3 mt-3">
          {tab === "active" && (
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadMerchants()}
                  placeholder="Search cashtag, name, store…" className="pl-9" />
              </div>
              <Button onClick={loadMerchants}>Search</Button>
            </div>
          )}

          {loading ? (
            <div className="p-6 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : tab === "active" ? (
            <Card className="divide-y divide-border">
              {merchants.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground">No merchants found.</div>
              )}
              {merchants.map((m) => (
                <div key={m.user_id} className="px-3 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">
                      {m.store_name || m.display_name}{" "}
                      <span className="text-muted-foreground">@{m.cashtag}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Joined {new Date(m.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="secondary">Active</Badge>
                    <Button variant="destructive" size="sm" onClick={() => suspend(m)}>Suspend</Button>
                  </div>
                </div>
              ))}
            </Card>
          ) : (
            <Card className="divide-y divide-border">
              {apps.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground">No {tab} applications.</div>
              )}
              {apps.map((a) => {
                const p = appProfiles[a.user_id];
                return (
                  <div key={a.id} className="px-3 py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        {a.store_name} <span className="text-muted-foreground">· {a.category}</span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {p ? `@${p.cashtag} · ${p.display_name}` : a.user_id.slice(0, 8)}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {new Date(a.created_at).toLocaleString()} · {a.country}
                        {a.website ? ` · ${a.website}` : ""}
                      </div>
                      {a.admin_notes && (
                        <div className="text-[11px] text-muted-foreground mt-1 italic">
                          Notes: {a.admin_notes}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      <Badge variant={a.status === "approved" ? "default" : a.status === "rejected" ? "destructive" : "secondary"}>
                        {a.status}
                      </Badge>
                      {a.status === "pending" && (
                        <Button size="sm" onClick={() => { setReviewing(a); setNotes(""); }}>Review</Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review: {reviewing?.store_name}</DialogTitle>
          </DialogHeader>
          {reviewing && (
            <div className="space-y-2 text-sm">
              <p><strong>Category:</strong> {reviewing.category}</p>
              <p><strong>Country:</strong> {reviewing.country}</p>
              {reviewing.website && <p><strong>Website:</strong> {reviewing.website}</p>}
              {reviewing.phone && <p><strong>Phone:</strong> {reviewing.phone}</p>}
              {reviewing.description && (
                <p className="text-muted-foreground whitespace-pre-wrap">{reviewing.description}</p>
              )}

              {reviewing.documents && reviewing.documents.length > 0 ? (
                <div className="pt-2 border-t border-border">
                  <p className="text-xs font-medium mb-1">Documents ({reviewing.documents.length})</p>
                  <ul className="space-y-1">
                    {reviewing.documents.map((d) => (
                      <li key={d.path} className="flex items-center justify-between gap-2 text-xs bg-muted/50 rounded-md px-2 py-1.5">
                        <div className="min-w-0">
                          <div className="truncate font-medium">{d.type.replace(/_/g, " ")}</div>
                          <div className="truncate text-muted-foreground">{d.name} · {(d.size / 1024).toFixed(0)} KB</div>
                        </div>
                        <Button size="sm" variant="outline" onClick={async () => {
                          const { data, error } = await supabase.storage
                            .from("merchant-docs").createSignedUrl(d.path, 300);
                          if (error || !data) { toast.error(error?.message || "Failed"); return; }
                          window.open(data.signedUrl, "_blank", "noopener");
                        }}>View</Button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No documents attached.</p>
              )}

              <Textarea placeholder="Admin notes (required for rejection)"
                value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} rows={3} />
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="destructive" onClick={() => review("rejected")} disabled={busy}>Reject</Button>
            <Button onClick={() => review("approved")} disabled={busy}>Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMerchants;
